/**
 * Otimização de imagens antes de aplicar ao VirtualBackgroundProcessor.
 *
 * Objetivo (out/2026): **manter a imagem de fundo nítida**. A versão anterior reduzia
 * para 640×360 e descia a qualidade JPEG até 0.15 para caber em 256 KB — o fundo ficava
 * visivelmente borrado/quadriculado atrás do participante. Agora:
 * - teto de 1280×720 (a câmera com processor publica no máximo 720p);
 * - JPEG começa em 0.85 e nunca desce de 0.6;
 * - se ainda passar de 1 MB, reduz as **dimensões** em degraus de 0.8 (mínimo 640×360)
 *   em vez de degradar a qualidade;
 * - resultado em cache por data URL (a mesma imagem é reaplicada a cada reconexão).
 */

export const MAX_IMAGE_WIDTH = 1280;
export const MAX_IMAGE_HEIGHT = 720;
export const MIN_IMAGE_WIDTH = 640;
export const MIN_IMAGE_HEIGHT = 360;
/** Tamanho alvo do data URL otimizado (bytes). */
export const MAX_FILE_SIZE_BYTES = 1024 * 1024;
export const JPEG_QUALITY_START = 0.85;
export const JPEG_QUALITY_MIN = 0.6;
export const JPEG_QUALITY_STEP = 0.05;
export const DOWNSCALE_STEP = 0.8;
const MAX_CACHE_ENTRIES = 6;

export interface ImageOptimizationResult {
  success: boolean;
  dataUrl?: string;
  originalSize?: number;
  optimizedSize?: number;
  width?: number;
  height?: number;
  quality?: number;
  error?: string;
}

const resultCache = new Map<string, ImageOptimizationResult>();

/** Limpa o cache (testes / troca de usuário). */
export function clearImageOptimizationCache(): void {
  resultCache.clear();
}

/**
 * Dimensões de saída: encaixa em MAX_IMAGE_WIDTH×MAX_IMAGE_HEIGHT preservando a
 * proporção; imagens menores que o teto não são ampliadas.
 */
export function computeTargetDimensions(
  width: number,
  height: number,
  maxWidth = MAX_IMAGE_WIDTH,
  maxHeight = MAX_IMAGE_HEIGHT,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) {
    return { width: maxWidth, height: maxHeight };
  }
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Próximo passo quando o resultado ainda está acima do alvo. Prioridade:
 * 1) baixar qualidade até JPEG_QUALITY_MIN; 2) só então reduzir dimensões (até o mínimo).
 * Retorna null quando não há mais o que fazer (aceita o tamanho atual).
 */
export function nextCompressionStep(current: {
  width: number;
  height: number;
  quality: number;
}): { width: number; height: number; quality: number } | null {
  const nextQuality = Math.round((current.quality - JPEG_QUALITY_STEP) * 100) / 100;
  if (nextQuality >= JPEG_QUALITY_MIN) {
    return { ...current, quality: nextQuality };
  }
  if (current.width > MIN_IMAGE_WIDTH || current.height > MIN_IMAGE_HEIGHT) {
    const width = Math.max(MIN_IMAGE_WIDTH, Math.round(current.width * DOWNSCALE_STEP));
    const height = Math.max(MIN_IMAGE_HEIGHT, Math.round(current.height * DOWNSCALE_STEP));
    return { width, height, quality: JPEG_QUALITY_MIN };
  }
  return null;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Falha ao carregar imagem'));
    img.src = src;
  });
}

/**
 * Redimensiona e comprime uma imagem (data URL) para uso como fundo virtual.
 */
export async function optimizeImageForBackground(
  imageDataUrl: string,
): Promise<ImageOptimizationResult> {
  const cached = resultCache.get(imageDataUrl);
  if (cached) {
    return cached;
  }

  try {
    const [, payload] = imageDataUrl.split(',');
    if (!imageDataUrl.startsWith('data:') || !payload) {
      return { success: false, error: 'Formato de imagem inválido' };
    }

    const img = await loadImage(imageDataUrl);
    const originalWidth = img.naturalWidth || img.width;
    const originalHeight = img.naturalHeight || img.height;
    const originalSize = imageDataUrl.length;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return { success: false, error: 'Não foi possível criar contexto de canvas' };
    }

    let step = {
      ...computeTargetDimensions(originalWidth, originalHeight),
      quality: JPEG_QUALITY_START,
    };
    let dataUrl = '';
    let drawnSize: { width: number; height: number } | null = null;

    // Loop limitado: ≤5 passos de qualidade + ≤4 reduções de tamanho.
    for (let attempt = 0; attempt < 12; attempt++) {
      if (!drawnSize || drawnSize.width !== step.width || drawnSize.height !== step.height) {
        canvas.width = step.width;
        canvas.height = step.height;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, step.width, step.height);
        drawnSize = { width: step.width, height: step.height };
      }
      dataUrl = canvas.toDataURL('image/jpeg', step.quality);
      if (dataUrl.length <= MAX_FILE_SIZE_BYTES) {
        break;
      }
      const next = nextCompressionStep(step);
      if (!next) {
        break;
      }
      step = next;
    }

    if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
      return { success: false, error: 'Falha ao gerar imagem otimizada' };
    }

    const result: ImageOptimizationResult = {
      success: true,
      dataUrl,
      originalSize,
      optimizedSize: dataUrl.length,
      width: step.width,
      height: step.height,
      quality: step.quality,
    };

    console.info('[ImageOptimization] fundo otimizado:', {
      from: `${originalWidth}x${originalHeight} (${(originalSize / 1024).toFixed(0)} KB)`,
      to: `${step.width}x${step.height} q${step.quality} (${(dataUrl.length / 1024).toFixed(0)} KB)`,
    });

    if (resultCache.size >= MAX_CACHE_ENTRIES) {
      const oldest = resultCache.keys().next().value;
      if (oldest !== undefined) resultCache.delete(oldest);
    }
    resultCache.set(imageDataUrl, result);
    return result;
  } catch (error) {
    console.error('[ImageOptimization] Erro ao otimizar imagem:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido',
    };
  }
}

/**
 * Data URLs sempre passam pela otimização (normaliza formato/dimensões e garante um
 * JPEG de tamanho previsível); URLs de arquivo (`/background-images/*.jpg`) não.
 */
export function shouldOptimizeImage(imageDataUrl: string): boolean {
  return imageDataUrl.startsWith('data:');
}
